import * as React from "react";
import * as S from "@styled";
import { connect } from "react-redux";
import * as _ from "lodash";
import Select from "shared/components/select/Select";

import { getSchemaTypeName } from "../../environmentVariables/model/GetSchemaTypeName";
import { SchemaType } from "../../environmentVariables/model/VariableSchema";
import { State } from "../model/State";
import { EnvironmentVariableEditor } from "../../environmentVariables/components/EnvironmentVariableEditor";
import { DispatchType, Utilities } from "../../shared/utilities";
import { addNewEnvironmentVariable } from "../actions/AddNewEnvironmentVariable";
import CustomModal from "shared/components/CustomModal";
import { Option } from "shared/model/Option";
import { ValidationError, ValidationErrorType } from "../model/ValidationData";

export const CreateEnvironmentVariableEditor: React.FC<CreateEnvironmentVariableEditorProps> = (props) => {
    const getDefaultState = (): CreateEnvironmentVariableEditorState => {
        const parameterParts = props.error.referenceValue!.split(".");
        const parentVariableName = parameterParts[1];

        return ({
            showModal: false,
            parentVariableName: parentVariableName
        })
    };

    const [state, setState] = React.useState<CreateEnvironmentVariableEditorState>(getDefaultState());

    React.useEffect(() => {
        const parameterParts = props.error.referenceValue!.split(".");
        const parentVariableName = parameterParts[1];

        setState(state => ({
            ...state,
            parentVariableName: parentVariableName,
        }));
    }, [props.error.referenceValue]);

    if (props.error.messageType !== ValidationErrorType.InvalidReference) {
        return <></>;
    }

    if (props.error.referenceValue?.toLowerCase().indexOf("#env.") !== 0) {
        return <></>;
    }

    const handleShow = () => {
        setState({
            ...state,
            showModal: true
        });
    };

    const handleClose = () => {
        // Clear the input field after adding a variable
        setState(getDefaultState());
    };

    const newEnvironmentVariableModal = () => {
        let title = FRENDS.Localizations.CreateNew + " " + FRENDS.Localizations.Variable.toLowerCase();
        if (state.parentVariableName) {
            title = title + " " + FRENDS.Localizations.ForVariable + " " + state.parentVariableName;
        }
        return (
            <CustomModal
                label="new-variable-modal"
                onClose={handleClose}
                show={state.showModal}
                shouldCloseOnOverlayClick={false}
                modalTitle={<h2>{title}</h2>}
                size="medium"
            >
                <CreateEnvironmentVariableEditorContent
                    addNewVariable={props.addNewVariable}
                    error={props.error}
                    forbiddenNames={props.forbiddenNames}
                    handleClose={handleClose}
                />
            </CustomModal>
        );
    };

    return (
        <>
            <S.OpenExternalLink role="link" onClick={handleShow} className="pointer">
                {FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Variable.toLowerCase()}
            </S.OpenExternalLink>
            {state?.showModal && newEnvironmentVariableModal()}
        </>
    );

};

export const CreateEnvironmentVariableEditorContent: React.FC<CreateEnvironmentVariableEditorContentProps> = (props) => {

    const getDefaultValue = (type: SchemaType) => {
        switch (type) {
            case SchemaType.String:
                return "";
            case SchemaType.Array:
                return [];
            case SchemaType.Secret:
                return "";
            case SchemaType.Number:
                return "0";
            case SchemaType.Boolean:
                return false;
        }
    };

    const getDefaultState = (): CreateEnvironmentVariableEditorContentState => {
        const parameterParts = props.error.referenceValue!.split(".");

        const parentVariableName = parameterParts[1];
        let variableName;
        if (parameterParts.length > 2) {
            variableName = parameterParts[2];
        }

        return ({
            parentVariableName: parentVariableName,
            name: variableName,
            value: getDefaultValue(SchemaType.String),
            type: SchemaType.String,
            saveDisabled: !parentVariableName || !variableName
        })
    };

    const [state, setState] = React.useState<CreateEnvironmentVariableEditorContentState>(getDefaultState());

    React.useEffect(() => {
        const parameterParts = props.error.referenceValue!.split(".");

        const parentVariableName = parameterParts[1];
        let variableName: string;
        if (parameterParts.length > 2) {
            variableName = parameterParts[2];
        }

        setState(state => ({
            ...state,
            name: variableName,
            parentVariableName: parentVariableName,
            saveDisabled: !variableName || !parentVariableName
        }));
    }, [props.error.referenceValue]);

    const nameRegex = /^[a-zA-ZöäåüÖÄÅÜ][a-zA-Z0-9_öäåüÖÄÅÜ]*$/;

    const getTypeOptions = (): SchemaType[] => {
        const schemaTypeKeys = Object.keys(SchemaType).map((s) => s as SchemaType);
        if (!state.parentVariableName) {
            return schemaTypeKeys;
        }
        return schemaTypeKeys.filter((type) => type !== SchemaType.Object);
    };

    const environmentVariableTypeOptions: Option[] = getTypeOptions().map((type) => ({
        label: getSchemaTypeName(type),
        value: type
    }))

    const nameChanged = (event: React.ChangeEvent<HTMLInputElement>, isParentVariable: boolean) => {
        const newValue = event.target.value;

        const validationError = validateName(newValue, isParentVariable);

        setState({
            ...state,
            ...(isParentVariable) ? {
                parentVariableName: newValue,
                parentNameValidationError: validationError
            } : {
                name: newValue,
                nameValidationError: validationError
            },
            saveDisabled: !!validationError
        });
    };

    const validateName = (candidate: string, isParentVariable: boolean) => {
        if (!candidate) {
            return FRENDS.Localizations.Validation_FieldRequired;
        }
        if (!nameRegex.test(candidate)) {
            return FRENDS.Localizations.EnvironmentVariables_KeyPattern;
        }
        if (!isParentVariable && props.forbiddenNames.some((forbiddenName) =>
            forbiddenName.toUpperCase() === candidate.toUpperCase())) {
            return FRENDS.Localizations.Validation_ElementsMustHaveUniqueNames;
        }
        return "";
    };

    const typeChanged = (event: Option) => {
        const newType: SchemaType = (SchemaType as { [key: string]: SchemaType })[event.value];
        setState({
            ...state,
            type: newType,
            value: getDefaultValue(newType)
        });
    };

    const handleValueUpdated = (schemaId: number, environmentId: number, newValue: any, version: number) => {
        setState({ ...state, value: newValue });
    };

    const handleNewSchemaClick = () => {

        if (!state.name) {
            Utilities.toastError("Variable name required.")
            return;
        }

        props.addNewVariable(
            state.type,
            state.name,
            state.parentVariableName,
            state.value
        );
        props.handleClose();
    };

    return (
        <>
            <S.FormGroup>
                <S.FormGroup variant={state.parentNameValidationError ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.Label htmlFor="parentName" id="newParentVariableLabel">
                        Parent {FRENDS.Localizations.Variable} {FRENDS.Localizations.Name.toLowerCase()}
                    </S.Label>
                    <S.Input
                        aria-label="parentName"
                        isFullWidth={true}
                        name="parentName"
                        type={"text"}
                        id="new-parent-variable-name-input"
                        value={state.parentVariableName}
                        onChange={(e) => nameChanged(e, true)}
                    />
                    {state.parentNameValidationError ? <div className="error">{state.parentNameValidationError}</div> : null}
                </S.FormGroup>
                <S.FormGroup variant={state.nameValidationError ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.Label htmlFor="name" id="newVariableLabel">
                        {FRENDS.Localizations.Variable} {FRENDS.Localizations.Name.toLowerCase()}
                    </S.Label>
                    <S.Input
                        aria-label="name"
                        isFullWidth={true}
                        name="name"
                        type={"text"}
                        id="new-variable-name-input"
                        value={state.name}
                        onChange={(e) => nameChanged(e, false)}
                    />
                    {state.nameValidationError ? <div className="error">{state.nameValidationError}</div> : null}
                </S.FormGroup>
                <S.FormGroup>
                    <S.Label htmlFor="type">{FRENDS.Localizations.Type}</S.Label>
                    <Select
                        classNamePrefix="react-select"
                        value={Utilities.valueFromId(environmentVariableTypeOptions, state.type)}
                        options={environmentVariableTypeOptions}
                        name="type"
                        id="new-variable-type-select"
                        onChange={typeChanged}
                        menuPortalTarget={document.body}
                    />
                </S.FormGroup>
                {state.type !== SchemaType.Object
                    ? <S.FormGroup>
                        <S.Label>{FRENDS.Localizations.Value} {FRENDS.Localizations.InEnvironment.toLowerCase()}: Development</S.Label>
                        <EnvironmentVariableEditor
                            environmentId={0}
                            value={state.value}
                            schemaId={0}
                            schemaType={state.type}
                            variableVersion={0}
                            updateValue={handleValueUpdated}
                            filterText=""
                        />
                    </S.FormGroup>
                    : null}
            </S.FormGroup>
            <S.FormGroup>
                <S.ButtonGroup $justifyContentEnd={true} gap={12}>
                    <S.Button variant={S.ButtonType.bordered} onClick={props.handleClose}>{FRENDS.Localizations.Cancel}</S.Button>
                    <S.Button onClick={handleNewSchemaClick} disabled={state.saveDisabled} id="create-new-variable-button">
                        {FRENDS.Localizations.CreateNew}
                    </S.Button>
                </S.ButtonGroup>
            </S.FormGroup>
        </>
    )
}

interface CreateEnvironmentVariableEditorContentProps {
    error: ValidationError;
    forbiddenNames: string[];
    addNewVariable: (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) => void;
    handleClose: () => void;
}

export interface CreateEnvironmentVariableEditorOwnProps {
    error: ValidationError;
    forbiddenNames?: string[];
    addNewVariable?: (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) => void;
}

interface CreateEnvironmentVariableEditorStateProps {
    forbiddenNames: string[];
}

interface CreateEnvironmentVariableEditorDispatchProps {
    addNewVariable: (type: SchemaType, name: string, parentName: string, value: string | boolean | string[] | undefined) => void;
}

interface CreateEnvironmentVariableEditorState {
    showModal: boolean;
    parentVariableName: string;
}

interface CreateEnvironmentVariableEditorContentState {
    parentVariableName: string;
    name?: string;
    value: string | boolean | string[] | undefined;
    type: SchemaType;
    nameValidationError?: string;
    parentNameValidationError?: string,
    saveDisabled: boolean;
}

export type CreateEnvironmentVariableEditorProps =
    CreateEnvironmentVariableEditorOwnProps &
    CreateEnvironmentVariableEditorStateProps &
    CreateEnvironmentVariableEditorDispatchProps;

function mapStateToProps(state: State, ownProps: CreateEnvironmentVariableEditorOwnProps): CreateEnvironmentVariableEditorStateProps {
    return {
        forbiddenNames: state.environmentVariables
    };
}

function mapDispatchToProps(dispatch: DispatchType<State>, ownProps: CreateEnvironmentVariableEditorOwnProps): CreateEnvironmentVariableEditorDispatchProps {
    return {
        addNewVariable: (type, name, parentName, value) => ownProps.addNewVariable ? ownProps.addNewVariable(type, name, parentName, value) : dispatch(addNewEnvironmentVariable(type, name, parentName, value))
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateEnvironmentVariableEditor);
